import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SchemaOrg from "@/components/base/SchemaOrg";

const siteUrl =
  (import.meta.env.VITE_SITE_URL as string) || "https://tbm-lab.genaiweb.ai";

const cookiePageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "クッキーポリシー | 株式会社TBM健康美容研究所",
  "description": "株式会社TBM健康美容研究所のクッキーポリシー。当サイトで利用するクッキーの種類・目的・無効化方法についてご案内しています。",
  "url": `${siteUrl}/cookie`,
  "publisher": {
    "@type": "Organization",
    "name": "株式会社TBM健康美容研究所",
    "url": siteUrl,
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "ホーム",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "クッキーポリシー",
      item: `${siteUrl}/cookie`,
    },
  ],
};

const sections = [
  {
    title: "1. クッキーとは",
    body: "クッキー（Cookie）とは、ウェブサイトを閲覧した際に、ご利用のブラウザに保存される小さなテキストファイルです。クッキーにより、サイトの訪問履歴や設定情報などが記録され、次回以降の訪問時に、より快適にサイトをご利用いただくことができます。",
  },
  {
    title: "2. 当サイトで利用するクッキー",
    body: "当サイトでは、以下の種類のクッキーを利用しています。",
    list: [
      "必須クッキー：サイトの基本的な機能（ページ表示、フォーム送信など）を提供するために必要なクッキーです。",
      "機能クッキー：お客様の選択・設定（言語設定など）を記憶し、利便性を向上させるためのクッキーです。",
      "分析クッキー：サイトの利用状況（アクセス数、閲覧ページなど）を統計的に分析し、サイトの品質向上に役立てるためのクッキーです。",
    ],
  },
  {
    title: "3. クッキーの利用目的",
    body: "当社は、クッキーを以下の目的で利用いたします。",
    list: [
      "サイトの利便性・操作性の向上のため",
      "アクセス状況の統計的な分析およびサイト改善のため",
      "お客様の興味・関心に応じた情報提供のため",
    ],
  },
  {
    title: "4. クッキーの無効化（オプトアウト）方法",
    body: "お客様は、ブラウザの設定を変更することにより、クッキーの受け入れを拒否したり、クッキーを削除したりすることができます。なお、クッキーを無効化した場合、当サイトの一部の機能が正常に動作しなくなる場合がありますので、あらかじめご了承ください。各ブラウザの設定方法については、ご利用のブラウザのヘルプ等をご確認ください。",
  },
  {
    title: "5. お問い合わせ窓口",
    body: "クッキーの取り扱いに関するお問い合わせは、下記までご連絡ください。",
    contact: {
      company: "株式会社TBM健康美容研究所",
      address: "〒370-0841 群馬県高崎市栄町13-1",
      tel: "027-310-2220",
      fax: "027-327-6344",
    },
  },
];

export default function CookiePage() {
  return (
    <div className="min-h-screen bg-white">
      <SchemaOrg data={cookiePageSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <Header />

      <main>
        {/* Page Header */}
        <section className="relative w-full pt-24 md:pt-32 pb-12 md:pb-16 bg-gamo-light-gray">
          <div className="section-container">
            <nav className="flex items-center gap-2 text-xs text-gamo-medium-gray mb-6">
              <a href="/" className="hover:text-gamo-green transition-colors">ホーム</a>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-gamo-dark-gray">クッキーポリシー</span>
            </nav>
            <h1 className="text-gamo-black text-2xl md:text-3xl lg:text-4xl font-bold tracking-wide">
              クッキーポリシー
            </h1>
            <p className="text-gamo-medium-gray text-sm mt-3">
              Cookie Policy
            </p>
            <div className="w-10 h-px bg-gamo-green mt-5" />
          </div>
        </section>

        {/* Content */}
        <section className="w-full bg-white py-12 md:py-16">
          <div className="section-container max-w-3xl">
            <p className="text-gamo-medium-gray text-sm leading-relaxed mb-10">
              株式会社TBM健康美容研究所（以下「当社」といいます）のウェブサイト（以下「当サイト」といいます）では、クッキーを使用しています。本ポリシーでは、当サイトで利用するクッキーの種類・目的・無効化方法についてご説明します。
            </p>

            <div className="space-y-10">
              {sections.map((section) => (
                <article key={section.title}>
                  <h2 className="text-gamo-black text-lg md:text-xl font-bold mb-3">
                    {section.title}
                  </h2>
                  <p className="text-gamo-dark-gray text-sm leading-relaxed">
                    {section.body}
                  </p>
                  {section.list && (
                    <ul className="mt-3 space-y-2">
                      {section.list.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-2 text-gamo-dark-gray text-sm leading-relaxed"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-gamo-green flex-shrink-0 mt-2"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.contact && (
                    <div className="mt-4 bg-gamo-light-gray border border-gamo-border-gray rounded-lg p-5 md:p-6">
                      <dl className="space-y-2 text-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                          <dt className="text-gamo-medium-gray w-24 flex-shrink-0">名称</dt>
                          <dd className="text-gamo-dark-gray font-medium">{section.contact.company}</dd>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                          <dt className="text-gamo-medium-gray w-24 flex-shrink-0">所在地</dt>
                          <dd className="text-gamo-dark-gray">{section.contact.address}</dd>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                          <dt className="text-gamo-medium-gray w-24 flex-shrink-0">電話番号</dt>
                          <dd className="text-gamo-dark-gray">{section.contact.tel}</dd>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                          <dt className="text-gamo-medium-gray w-24 flex-shrink-0">FAX</dt>
                          <dd className="text-gamo-dark-gray">{section.contact.fax}</dd>
                        </div>
                      </dl>
                    </div>
                  )}
                </article>
              ))}
            </div>

            <p className="text-gamo-medium-gray text-xs leading-relaxed mt-12 pt-6 border-t border-gamo-border-gray">
              制定日：2026年9月17日
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}